<template>
  <div>
    <v-card-text>
      <div class="row row--space-between">
        <div style="width: 45%">
          <v-text-field
            v-model.number="displayableCacheSettings.maxConcurrentDownloads"
            label="Max concurrent download threads"
            :rules="[rules.notANumber, rules.nonNegative]"
            @change="
              updateMaxConcurrentDownloads(
                displayableCacheSettings.maxConcurrentDownloads
              )
            "
            type="number"
          ></v-text-field>
        </div>
        <div style="width: 45%">
          <v-select
            :items="cacheSizes"
            item-value="value"
            item-text="text"
            v-model.number="displayableCacheSettings.chunkSize"
            label="Download chunk size"
            :rules="[rules.notANumber, rules.nonNegative]"
            @change="updateChunkSize(displayableCacheSettings.chunkSize)"
            type="number"
          ></v-select>
        </div>
      </div>
    </v-card-text>
    <h3>Memory Cache</h3>
    <v-card-text>
      <div class="row row--space-between">
        <div style="width: 45%">
          <v-text-field
            :suffix="megaBytes"
            v-model.number="displayableCacheSettings.memory.sizeLimit"
            label="Memory cache size limit"
            :rules="[rules.notANumber, rules.nonNegative]"
            @change="
              updateMemoryCacheSizeLimit(
                bytesFromMB(displayableCacheSettings.memory.sizeLimit)
              )
            "
            type="number"
          ></v-text-field>
        </div>
        <div style="width: 45%">
          <v-text-field
            suffix="%"
            v-model.number="
              displayableCacheSettings.memory.compactionPercentage
            "
            label="Memory cache recycling percentage"
            :rules="[rules.notANumber, rules.nonNegative, rules.percent]"
            @change="
              updateMemoryCacheCompactionPercentage(
                mathPercentFromInteger(
                  displayableCacheSettings.memory.compactionPercentage
                )
              )
            "
            type="number"
          ></v-text-field>
        </div>
      </div>
    </v-card-text>
    <h3>Disk Cache</h3>
    <v-card-text>
      <v-row align="center">
        <v-checkbox
          class="mr-10"
          v-model="displayableCacheSettings.disk.enabled"
          label="Disk cache enabled"
          @change="
            updateDiskCacheEnabled(displayableCacheSettings.disk.enabled)
          "
        ></v-checkbox>
        <v-text-field
          :disabled="!displayableCacheSettings.disk.enabled"
          v-model="displayableCacheSettings.disk.path"
          label="Disk cache path"
          :rules="[rules.required]"
          @change="updateDiskCachePath(displayableCacheSettings.disk.path)"
        ></v-text-field>
      </v-row>
      <div class="row row--space-between">
        <div style="width: 45%">
          <v-text-field
            :suffix="gigaBytes"
            :disabled="!displayableCacheSettings.disk.enabled"
            v-model.number="displayableCacheSettings.disk.sizeLimit"
            label="Disk cache size limit"
            :rules="[rules.notANumber, rules.nonNegative]"
            @change="
              updateDiskCacheSizeLimit(
                bytesFromGB(displayableCacheSettings.disk.sizeLimit)
              )
            "
            type="number"
          ></v-text-field>
        </div>
        <div style="width: 45%">
          <v-text-field
            suffix="%"
            :disabled="!displayableCacheSettings.disk.enabled"
            v-model.number="displayableCacheSettings.disk.compactionPercentage"
            label="Disk cache recycling percentage"
            :rules="[rules.notANumber, rules.nonNegative, rules.percent]"
            @change="
              updateDiskCacheCompactionPercentage(
                mathPercentFromInteger(
                  displayableCacheSettings.disk.compactionPercentage
                )
              )
            "
            type="number"
          ></v-text-field>
        </div>
      </div>
    </v-card-text>
  </div>
</template>

<script>
import { HubConnection } from "@microsoft/signalr";
import { DeepCopy } from "../../services/deep-copy";

const KiloBytes = "KB";
const MegaBytes = "MB";
const GigaBytes = "GB";
const Kilo = 1024;
const Mega = Kilo * Kilo;
const Giga = Mega * Kilo;

export default {
  name:  "UpdateCacheSettingsForm",
  props: {
    connection:    HubConnection,
    cacheSettings: Object,
  },
  data: function() {
    return {
      kiloBytes:                KiloBytes,
      megaBytes:                MegaBytes,
      gigaBytes:                GigaBytes,
      displayableCacheSettings: this.mapTyDisplayableCacheSettings(
        this.cacheSettings
      ),
      rules: {
        notANumber:  (value) => !isNaN(value) || "Number required",
        nonNegative: (value) => value >= 0 || "Non-negative number required",
        required:    (value) => !!value || "Required",
        percent:     (value) => value > 0 && value < 100 || "Percentage has to be between 0 and 100",
      },
      cacheSizes: [
        this.selectSizeValue(256, KiloBytes),
        this.selectSizeValue(512, KiloBytes),
        this.selectSizeValue(1, MegaBytes),
        this.selectSizeValue(2, MegaBytes),
        this.selectSizeValue(4, MegaBytes),
        this.selectSizeValue(8, MegaBytes),
        this.selectSizeValue(16, MegaBytes),
        this.selectSizeValue(32, MegaBytes),
      ],
    };
  },
  watch: {
    cacheSettings(newCacheSettings) {
      this.displayableCacheSettings = this.mapTyDisplayableCacheSettings(
        newCacheSettings
      );
    },
  },
  methods: {
    mapTyDisplayableCacheSettings(cacheSettings) {
      let copiedCacheSettings = DeepCopy(cacheSettings);
      copiedCacheSettings.disk.sizeLimit = this.gBFromBytes(
        cacheSettings.disk.sizeLimit
      );
      copiedCacheSettings.memory.sizeLimit = this.mBFromBytes(
        cacheSettings.memory.sizeLimit
      );
      copiedCacheSettings.disk.compactionPercentage = this.integerPercentFromMath(
        cacheSettings.disk.compactionPercentage
      );
      copiedCacheSettings.memory.compactionPercentage = this.integerPercentFromMath(
        cacheSettings.memory.compactionPercentage
      );
      return copiedCacheSettings;
    },
    mBFromBytes(value) {
      return value / Mega;
    },
    bytesFromMB(value) {
      return value * Mega;
    },
    gBFromBytes(value) {
      return value / Giga;
    },
    bytesFromGB(value) {
      return value * Giga;
    },
    integerPercentFromMath(value) {
      return value * 100;
    },
    mathPercentFromInteger(value) {
      return value / 100;
    },
    selectSizeValue(size, scale) {
      switch (scale) {
      case KiloBytes:
        return { value: size * Kilo, text: size.toString() + scale };
      case MegaBytes:
        return { value: size * Mega, text: size.toString() + scale };
      }
    },
    updateMemoryCacheSizeLimit(newLimit) {
      return this.handleInvocation(
        this.connection.invoke("UpdateMemoryCacheSizeLimit", newLimit)
      );
    },
    updateMemoryCacheCompactionPercentage(newCompactionPercentage) {
      return this.handleInvocation(
        this.connection.invoke(
          "UpdateMemoryCacheCompactionPercentage",
          newCompactionPercentage
        )
      );
    },
    updateDiskCacheSizeLimit(newLimit) {
      return this.handleInvocation(
        this.connection.invoke("UpdateDiskCacheSizeLimit", newLimit)
      );
    },
    updateDiskCacheEnabled(newEnabled) {
      return this.handleInvocation(
        this.connection.invoke("UpdateDiskCacheEnabled", newEnabled)
      );
    },
    updateDiskCacheCompactionPercentage(newCompactionPercentage) {
      return this.handleInvocation(
        this.connection.invoke(
          "UpdateDiskCacheCompactionPercentage",
          newCompactionPercentage
        )
      );
    },
    updateDiskCachePath(newPath) {
      return this.handleInvocation(
        this.connection.invoke("UpdateDiskCachePath", newPath)
      );
    },
    updateMaxConcurrentDownloads(newMaxConcurrentDownload) {
      return this.handleInvocation(
        this.connection.invoke(
          "UpdateMaxConcurrentDownloads",
          newMaxConcurrentDownload
        )
      );
    },
    updateChunkSize(newChunkSize) {
      return this.handleInvocation(
        this.connection.invoke("UpdateChunkSize", newChunkSize)
      );
    },
    handleInvocation(invocation) {
      return invocation
        .then((response) => {
          if (response.resultKind === "Success") {
            this.$emit("cache-settings-updated", response.entity);
          } else {
            this.$emit("failure", response.reason);
          }
        })
        .catch((reason) => {
          this.$emit("failure", reason);
        });
    },
  },
};
</script>

<style scoped></style>
